/* eslint-disable no-restricted-globals, no-restricted-properties */
// The ONLY file in the app allowed to touch window.localStorage / window.sessionStorage
// directly. Everything else must go through src/services/storage.
//
// Reading `window.localStorage` is a getter that can THROW (not return null):
//   - Android in-app WebViews with DOM storage disabled (the WebView default)
//   - Chrome set to "Block all cookies" / site data blocked for the origin
//   - cross-origin iframes under storage partitioning
// A single unguarded access in a lifecycle method takes the whole app down, which is
// exactly what happened on /scout (SecurityError, 2026-07-26).

/**
 * Build an in-memory driver. Used as the last-resort fallback so a browser that
 * refuses storage degrades to "works for this session" instead of crashing.
 * @returns {object} A driver backed by a Map.
 */
export function createMemoryDriver() {
  const memory = new Map()
  return {
    name: 'memory',
    durable: false,
    get: key => (memory.has(key) ? memory.get(key) : null),
    set: (key, value) => {
      memory.set(key, value)
    },
    remove: key => {
      memory.delete(key)
    },
    keys: () => Array.from(memory.keys()),
    clear: () => {
      memory.clear()
    },
  }
}

/**
 * Probe a Web Storage area once and return a driver for it, or null if unavailable.
 * The probe does a real write/remove because some browsers expose the object but
 * throw on first write.
 * @param {'localStorage'|'sessionStorage'} kind - Which storage area to probe.
 * @returns {object|null} A driver, or null when the area is unusable.
 */
export function probe(kind) {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    const area = window[kind]
    if (!area) {
      return null
    }
    const probeKey = '__ef_storage_probe__'
    area.setItem(probeKey, '1')
    area.removeItem(probeKey)
    return {
      name: kind,
      durable: kind === 'localStorage',
      get: key => area.getItem(key),
      set: (key, value) => area.setItem(key, value),
      remove: key => area.removeItem(key),
      keys: () => Object.keys(area),
      clear: () => area.clear(),
    }
  } catch (e) {
    return null
  }
}
